import React, { useState } from "react";
import { T } from "../theme";
import InvoicePill from "../components/InvoicePill";
import HappenedToggle from "../components/HappenedToggle";
import SessionPill from "../components/SessionPill";
import AddRowButton from "../components/AddRowButton";
import EditableDate, { formatSessionDate } from "../components/EditableDate";
import { calcSessionBlock, sortSessionsByDate, sessionsForBlock, summarizeBlock } from "./calc";
import Pill from "../client/Pill";

const CLIENT_SESSION = { done: ["good", "Delivered"], cancelled: ["neutral", "Cancelled"], scheduled: ["neutral", "Booked"] };

// `sessionBlock` module — a prepaid block of N sessions, counted down.
//
// Standing config (Finance Config):  { purchased, blockNo, opened, label, period,
//                                      value (ex GST) or rate, invoices: [{ number, date, sessions, amount, status }] }
// Facts (Finance Data):  sessionBlock.sessions[] of { id, date, status, block }
//                        status: scheduled | done | cancelled
//                        sessionBlock.history[] of completed blocks, each
//                        { blockNo, purchased, label, opened, closed, sessions }
// The card shows the CURRENT block; completed blocks render as summary lines.
//
// Layout and behaviour follow docs/design/niru-block-tracker-mockup.html;
// palette is the real theme, not the mockup's.

const eyebrow = { fontSize: 11, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: T.fgMuted };

const money = n => `$${Math.round(n).toLocaleString()}`;

function shortDate(iso) {
  if (!iso) return "";
  return new Date(iso + "T00:00:00").toLocaleDateString("en-NZ", { month: "short", year: "numeric" });
}

// `readOnly` (the client page): no toggles, add or delete; dates and status are static.
export default function SessionBlock({ block, sessions, history, onSetStatus, onSetDate, onAdd, onDelete, readOnly = false }) {
  const [adding, setAdding] = useState(false);
  const [newDate, setNewDate] = useState(() => new Date().toLocaleDateString("en-CA"));
  const { purchased, delivered, status, value, perSession, invoices, toInvoice, owing, fundingState } = calcSessionBlock(block, sessions);
  const ordered = sortSessionsByDate(sessionsForBlock(block, sessions));
  const past = (history || []).map(summarizeBlock).sort((a, b) => a.blockNo - b.blockNo);
  const blockRef = [
    `Block ${String(block.blockNo || 1).padStart(2, "0")}`,
    block.period ? block.period : (block.opened ? `opened ${formatSessionDate(block.opened)}` : ""),
  ].filter(Boolean).join(" · ");

  const submitAdd = () => {
    onAdd(newDate);
    setNewDate(new Date().toLocaleDateString("en-CA")); setAdding(false);
  };

  return (
    <>
      {/* The block ledger */}
      <div style={{ background: T.canvas, borderRadius: T.radiusMd, border: `1px solid ${T.borderSubtle}`, padding: "24px 26px 22px", marginBottom: 24, boxShadow: T.shadowSm }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, flexWrap: "wrap", marginBottom: 18 }}>
          <div>
            <div style={eyebrow}>{block.label || "Coaching block"}</div>
            <div style={{ fontSize: 12, color: T.fgMuted, marginTop: 4 }}>{blockRef}</div>
          </div>
        </div>

        {/* Slots: filled → next (dashed) → empty */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 20 }}>
          {Array.from({ length: purchased }, (_, i) => {
            const filled = i < delivered;
            const next = i === delivered;
            return (
              <div key={i} style={{
                flex: "1 1 0", minWidth: 44, height: 52, borderRadius: T.radiusSm,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 13, fontWeight: 600, fontFamily: T.fontSans, transition: "all 0.18s ease",
                background: filled ? T.root : T.canvas,
                color: filled ? T.fgOnDark : next ? T.fgSecondary : T.borderStrong,
                border: filled ? `1.5px solid ${T.root}` : next ? `1.5px dashed ${T.threadline}` : `1.5px solid ${T.borderSubtle}`,
              }}>{i + 1}</div>
            );
          })}
        </div>

        <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
          <div style={{ fontSize: 34, fontWeight: 700, lineHeight: 1, letterSpacing: "-0.02em", color: T.root }}>
            {status === "complete" ? "Block complete" : `${delivered} of ${purchased}`}
          </div>
          <div style={{ fontSize: 13, color: T.fgMuted }}>{status === "complete" ? `all ${purchased} sessions delivered` : "sessions delivered"}</div>
        </div>

        {/* Invoicing: the block is billed in tranches; list them, and what's still to raise */}
        {value > 0 && (
          <div style={{ marginTop: 16, paddingTop: 14, borderTop: `1px solid ${T.borderSubtle}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: T.root }}>Block value {money(value)} <span style={{ fontWeight: 400, color: T.fgMuted }}>+ GST · {money(perSession)} per session</span></span>
              <InvoicePill invoice={fundingState} />
            </div>
            <div style={{ marginTop: 8 }}>
              {invoices.map(inv => (
                <div key={inv.number || inv.date} style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10, fontSize: 12, padding: "5px 0", borderTop: `1px solid ${T.borderSubtle}`, color: T.fgSecondary }}>
                  <span><strong style={{ color: T.root }}>{inv.number}</strong>{inv.date ? ` · ${formatSessionDate(inv.date)}` : ""}{inv.sessions ? ` · ${inv.sessions} session${inv.sessions === 1 ? "" : "s"}` : ""}</span>
                  <span style={{ display: "flex", alignItems: "baseline", gap: 8 }}><span style={{ fontWeight: 600, color: T.root }}>{money(inv.amount || 0)}</span><InvoicePill invoice={inv.status} /></span>
                </div>
              ))}
              {toInvoice > 0 && (() => {
                const n = Math.round(toInvoice / perSession);
                const deliveredUnpaid = Math.round(owing / perSession);
                return (
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10, fontSize: 12, padding: "5px 0", borderTop: `1px solid ${T.borderSubtle}`, color: owing > 0 ? T.statusRisk : T.fgSecondary }}>
                    <span><strong>Next invoice</strong> · {n} session{n === 1 ? "" : "s"}{deliveredUnpaid > 0 ? ` · ${deliveredUnpaid} already delivered` : ""}</span>
                    <span style={{ display: "flex", alignItems: "baseline", gap: 8 }}><span style={{ fontWeight: 600 }}>{money(toInvoice)}</span><InvoicePill invoice="pending" /></span>
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {status === "last" && (
          <div style={{ marginTop: 18, background: T.statusRiskBg, borderLeft: `3px solid ${T.statusRisk}`, borderRadius: T.radiusSm, padding: "12px 14px", fontSize: 13, color: T.statusRisk, lineHeight: 1.5 }}>
            {readOnly ? <><strong>One session left in this block.</strong> We will talk about what comes next at the next session.</> : <><strong>Last session in this block.</strong> Raise rebooking at the next session, not after it.</>}
          </div>
        )}
        {status === "complete" && (
          <div style={{ marginTop: 18, background: T.statusOnBg, borderLeft: `3px solid ${T.statusOn}`, borderRadius: T.radiusSm, padding: "12px 14px", fontSize: 13, color: T.statusOn, lineHeight: 1.5 }}>
            {readOnly ? <><strong>Block complete.</strong> All {purchased} sessions delivered.</> : <><strong>Block complete.</strong> All {purchased} sessions delivered. Have the next-block conversation before booking anything further.</>}
          </div>
        )}
      </div>

      {/* Completed blocks */}
      {past.length > 0 && (
        <div style={{ background: T.bgNeutral, borderRadius: T.radiusMd, border: `1px solid ${T.borderSubtle}`, padding: "12px 18px", marginBottom: 24 }}>
          <div style={{ ...eyebrow, marginBottom: 8 }}>Previous blocks</div>
          {past.map(b => (
            <div key={b.blockNo} style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, padding: "6px 0", borderTop: `1px solid ${T.borderSubtle}` }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: T.fgSecondary }}>Block {String(b.blockNo).padStart(2, "0")}<span style={{ fontWeight: 400, color: T.fgMuted, marginLeft: 8 }}>{shortDate(b.opened)}{b.closed ? ` – ${shortDate(b.closed)}` : ""}</span></span>
              <span style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
                {b.value > 0 && <span style={{ fontSize: 12, color: T.fgMuted }}>{money(b.value)} + GST</span>}
                <InvoicePill invoice={b.invoice} />
                <span style={{ fontSize: 12, color: b.delivered >= b.purchased ? T.statusOn : T.fgMuted, fontWeight: 600 }}>{b.delivered} of {b.purchased} delivered{b.delivered >= b.purchased ? " ✓" : ""}</span>
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Sessions in the current block */}
      <div style={{ ...eyebrow, margin: "28px 0 10px" }}>Sessions{block.blockNo ? ` · block ${String(block.blockNo).padStart(2, "0")}` : ""}</div>
      <div style={{ background: T.canvas, borderRadius: T.radiusMd, border: `1px solid ${T.borderSubtle}`, boxShadow: T.shadowSm, marginBottom: 24 }}>
        {ordered.length === 0 && !adding && (
          <p style={{ padding: "16px 18px", fontSize: 12, color: T.fgMuted, fontStyle: "italic" }}>{readOnly ? "No sessions booked yet." : "No sessions yet — add the first one below."}</p>
        )}
        {ordered.map((s, i) => (
          <div key={s.id} className="session-row" style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 18px", borderBottom: `1px solid ${T.borderSubtle}` }}>
            <div style={{ fontSize: 12, color: T.fgMuted, width: 22, flexShrink: 0 }}>{String(i + 1).padStart(2, "0")}</div>
            <div className="row-label" style={{ flex: 1, minWidth: 0 }}>
              {readOnly ? <span style={{ fontSize: 13, fontWeight: 600, color: s.status === "cancelled" ? T.fgMuted : T.root, textDecoration: s.status === "cancelled" ? "line-through" : "none" }}>{s.date ? formatSessionDate(s.date) : "Date to confirm"}</span> : <EditableDate value={s.date} onChange={v => onSetDate && onSetDate(s.id, v)} muted={s.status === "cancelled"} strike={s.status === "cancelled"} />}
              <div style={{ marginTop: 4 }}>{readOnly ? <Pill tone={(CLIENT_SESSION[s.status] || CLIENT_SESSION.scheduled)[0]}>{(CLIENT_SESSION[s.status] || CLIENT_SESSION.scheduled)[1]}</Pill> : <SessionPill status={s.status} />}</div>
            </div>
            {perSession > 0 && <span style={{ fontSize: 12, color: s.status === "done" ? T.fgSecondary : T.borderStrong, minWidth: 48, textAlign: "right" }}>{s.status === "done" ? `−${money(perSession)}` : "—"}</span>}
            {!readOnly && <HappenedToggle value={s.status} onSet={key => onSetStatus(s.id, key)} />}
            {!readOnly && <button onClick={() => onDelete(s.id, s.date ? formatSessionDate(s.date) : "this session")} title="Remove session" style={{ width: 18, height: 18, border: "none", background: "transparent", cursor: "pointer", fontSize: 11, color: T.fgMuted, opacity: 0.4, padding: 0 }} onMouseEnter={e => e.currentTarget.style.opacity = 1} onMouseLeave={e => e.currentTarget.style.opacity = 0.4}>✕</button>}
          </div>
        ))}
        {readOnly ? null : adding ? (
          <div style={{ padding: "14px 18px", display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <input type="date" value={newDate} onChange={e => setNewDate(e.target.value)} autoFocus onKeyDown={e => e.key === "Enter" && submitAdd()} style={{ padding: "8px 12px", fontSize: 13, border: `1px solid ${T.threadline}`, borderRadius: T.radiusSm, outline: "none", fontFamily: T.fontSans, color: T.root }} />
            <button onClick={submitAdd} style={{ padding: "8px 16px", fontSize: 12, fontWeight: 600, color: T.fgOnDark, background: T.root, border: "none", borderRadius: T.radiusSm, cursor: "pointer", fontFamily: T.fontSans }}>Add</button>
            <button onClick={() => { setAdding(false); setNewDate(new Date().toLocaleDateString("en-CA")); }} style={{ padding: "8px 12px", fontSize: 12, color: T.fgMuted, background: "none", border: "none", cursor: "pointer", fontFamily: T.fontSans }}>Cancel</button>
          </div>
        ) : (
          <AddRowButton label="+ Add session" onClick={() => setAdding(true)} rounded />
        )}
      </div>
    </>
  );
}
